import React, { useEffect, useMemo, useState } from "react";
import Header from "../../master/SideBar";
import Nav from "../../master/NavBar";
import {
  BankList,
  BankMoneyTransfer,
  BankTransferList,
} from "../../../api/BankService";
import { closeModal, SweetAlertShow, LoadingDiv } from "../../master/Master";
import { imgBaseUrl } from "../../../api/api";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import BankLayout from "../../Accounts/BankLayout";
import usePrivateLink from "../../config/usePrivateLink";

export default function FundTransfer() {
    const hasAccess = usePrivateLink();
  
  const [loading, setLoading] = useState(false);
  const [banks, setBanks] = useState([]);
  const [transfers, setTransfers] = useState([]);

  const [selectedAccount, setSelectedAccount] = useState("");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const [page, setPage] = useState(1);

  const [imgModal, setImgModal] = useState({
    show: false,
    src: "",
  });

  const [proofImage, setProofImage] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);

  const [fromBank, setFromBank] = useState("");
  const [toBank, setToBank] = useState("");

  /* =========================================================
     IMAGE PREVIEW
  ========================================================= */

  useEffect(() => {
    if (!proofImage) {
      setProofPreview(null);
      return;
    }

    const url = URL.createObjectURL(proofImage);
    setProofPreview(url);

    return () => URL.revokeObjectURL(url);
  }, [proofImage]);

  /* =========================================================
     GET BANKS
  ========================================================= */

  const getBanks = async () => {
    setLoading(true);

    try {
      const response = await BankList();

      if (response?.data?.status) {
        setBanks(response?.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     GET TRANSFERS
  ========================================================= */

  const getTransfers = async () => {
    const addUrl =
      `&start_date=${start}` +
      `&end_date=${end}` +
      `&account_no=${selectedAccount}` +
      `&page=${page}`;

    setLoading(true);

    try {
      const response = await BankTransferList(addUrl);

      if (response?.data?.status) {
        setTransfers(response?.data?.data || []);
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getBanks();
  }, []);

  useEffect(() => {
    getTransfers();
  }, [start, end, selectedAccount, page]);

  /* =========================================================
     SUMMARY
  ========================================================= */

  const summary = useMemo(() => {
    const transferData = transfers?.data || [];

    const totalAmount = transferData.reduce(
      (sum, item) => sum + Number(item?.amount || 0),
      0,
    );

    const proofCount = transferData.filter((item) => item?.img).length;

    return {
      total: transfers?.total || 0,
      totalAmount,
      proofCount,
      currentPage: transferData.length,
    };
  }, [transfers]);

  /* =========================================================
     EXPORT
  ========================================================= */

  const exportToExcel = () => {
    if (!transfers?.data?.length) {
      SweetAlertShow("info", "No Data", "There are no transfers to export.");
      return;
    }

    const exportData = transfers.data.map((item, index) => ({
      "#": index + 1,

      "From Account": item?.from_bank?.account_no || "-",

      "From Owner": item?.from_bank?.owner_name || "-",

      "To Account": item?.to_bank?.account_no || "-",

      "To Owner": item?.to_bank?.owner_name || "-",

      Amount: Number(item?.amount || 0),

      "Transfer Date": item?.transfer_date || "-",

      Description: item?.description || "-",

      "Proof Available": item?.img ? "Yes" : "No",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);

    worksheet["!cols"] = [
      { wch: 6 },
      { wch: 25 },
      { wch: 30 },
      { wch: 25 },
      { wch: 30 },
      { wch: 16 },
      { wch: 18 },
      { wch: 45 },
      { wch: 18 },
    ];

    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Bank Transfers");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const data = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    saveAs(
      data,
      `Bank_Transfers_${new Date().toISOString().split("T")[0]}.xlsx`,
    );
  };

  /* =========================================================
     TRANSFER SUBMIT
  ========================================================= */

  const handleTransferSubmit = async (e) => {
    e.preventDefault();

    if (fromBank === toBank) {
      SweetAlertShow(
        "warning",
        "Invalid Transfer",
        "From and To accounts cannot be the same.",
      );
      return;
    }

    setLoading(true);

    try {
      const formData = new FormData(e.target);

      const response = await BankMoneyTransfer(formData);

      if (response?.data?.status) {
        closeModal("FundTransferModal");

        e.target.reset();

        setFromBank("");
        setToBank("");
        setProofImage(null);

        SweetAlertShow(
          "success",
          "Transfer Successful",
          response?.data?.message,
        );

        getTransfers();
      } else {
        SweetAlertShow("info", "Warning", response?.data?.message);
      }
    } catch (error) {
      SweetAlertShow(
        "error",
        "Error",
        error?.response?.data?.message ?? error?.message,
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     RESET FILTERS
  ========================================================= */

  const resetFilters = () => {
    setStart("");
    setEnd("");
    setSelectedAccount("");
    setPage(1);
  };

  /* =========================================================
     FORMAT DATE
  ========================================================= */

  const formatDate = (date) => {
    if (!date) return "--";

    return new Date(date).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  /* =========================================================
     ACCOUNT DISPLAY
  ========================================================= */

  const getAccountName = (bank) => {
    if (!bank) return "Unknown Account";

    return bank?.account_no || bank?.owner_name || "Unknown Account";
  };

  return (
    <>
      {loading && <LoadingDiv />}

      <div className="layout-wrapper layout-content-navbar">
        <div className="layout-container">
          <Header />

          <div className="layout-page">
            <Nav />

            <div className="container-fluid fund-transfer-page">
              <div className="row">
                <BankLayout />

                <div className="col-12">
                  {/* =================================================
                      PAGE HEADER
                  ================================================= */}

                  <div className="transfer-page-header">
                    <div className="transfer-title-wrapper">
                      <div className="transfer-page-icon">
                        <i className="ri-exchange-funds-line"></i>
                      </div>

                      <div>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <h3 className="mb-0 fw-bold">Fund Transfers</h3>

                          <span className="badge bg-label-primary rounded-pill">
                            {summary.total} Transfers
                          </span>
                        </div>

                        <p className="text-muted mb-0 mt-1">
                          Transfer money between business accounts and track
                          every transaction.
                        </p>
                      </div>
                    </div>

                    <div className="transfer-header-actions">

                      {hasAccess("/fund/transfer/add") && (
                        <button
                          type="button"
                          className="btn btn-primary"
                          data-bs-toggle="modal"
                          data-bs-target="#FundTransferModal"
                        >
                          <i className="ri-add-line me-1"></i>
                          New Transfer
                        </button>
                      )}

                      {hasAccess("/fund/transfer/export") && (
                        <button
                          type="button"
                          className="btn btn-label-success"
                          onClick={exportToExcel}
                        >
                          <i className="ri-file-excel-2-line me-1"></i>
                          Export
                        </button>
                      )}


                      <button
                        type="button"
                        className="btn btn-label-secondary btn-icon"
                        title="Refresh"
                        onClick={getTransfers}
                      >
                        <i className="ri-refresh-line"></i>
                      </button>
                    </div>
                  </div>

                  {/* =================================================
                      SUMMARY CARDS
                  ================================================= */}

                  <div className="row g-3 mb-4">
                    <div className="col-xl-4 col-md-6">
                      <div className="transfer-summary-card">
                        <div className="summary-icon bg-label-primary">
                          <i className="ri-exchange-dollar-line"></i>
                        </div>

                        <div>
                          <span className="summary-label">Total Transfers</span>

                          <h4 className="mb-0 fw-bold">{summary.total}</h4>

                          <small className="text-muted">
                            Matching transactions
                          </small>
                        </div>
                      </div>
                    </div>

                    <div className="col-xl-4 col-md-6">
                      <div className="transfer-summary-card">
                        <div className="summary-icon bg-label-success">
                          <i className="ri-money-rupee-circle-line"></i>
                        </div>

                        <div>
                          <span className="summary-label">
                            Current Page Amount
                          </span>

                          <h4 className="mb-0 fw-bold">
                            ₹{" "}
                            {summary.totalAmount.toLocaleString("en-IN", {
                              maximumFractionDigits: 2,
                            })}
                          </h4>

                          <small className="text-muted">
                            Total shown on this page
                          </small>
                        </div>
                      </div>
                    </div>

                    <div className="col-xl-4 col-md-6">
                      <div className="transfer-summary-card">
                        <div className="summary-icon bg-label-info">
                          <i className="ri-image-line"></i>
                        </div>

                        <div>
                          <span className="summary-label">Transfer Proofs</span>

                          <h4 className="mb-0 fw-bold">{summary.proofCount}</h4>

                          <small className="text-muted">
                            Transactions with proof
                          </small>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* =================================================
                      FILTER CARD
                  ================================================= */}

                  <div className="card border-0 shadow-sm rounded-4 mb-4">
                    <div className="card-body p-4">
                      <div className="filter-header">
                        <div>
                          <h6 className="fw-bold mb-1">
                            <i className="ri-filter-3-line text-primary me-2"></i>
                            Filter Transfers
                          </h6>

                          <small className="text-muted">
                            Narrow down transfers by date or account.
                          </small>
                        </div>

                        {(start || end || selectedAccount) && (
                          <button
                            type="button"
                            className="btn btn-sm btn-label-secondary"
                            onClick={resetFilters}
                          >
                            <i className="ri-close-line me-1"></i>
                            Clear Filters
                          </button>
                        )}
                      </div>

                      <div className="row g-3 mt-1">
                        <div className="col-xl-3 col-md-6">
                          <div className="form-floating form-floating-outline">
                            <input
                              type="date"
                              className="form-control"
                              value={start}
                              onChange={(e) => {
                                setStart(e.target.value);
                                setPage(1);
                              }}
                            />

                            <label>
                              <i className="ri-calendar-line me-1"></i>
                              From Date
                            </label>
                          </div>
                        </div>

                        <div className="col-xl-3 col-md-6">
                          <div className="form-floating form-floating-outline">
                            <input
                              type="date"
                              className="form-control"
                              value={end}
                              min={start || undefined}
                              onChange={(e) => {
                                setEnd(e.target.value);
                                setPage(1);
                              }}
                            />

                            <label>
                              <i className="ri-calendar-check-line me-1"></i>
                              To Date
                            </label>
                          </div>
                        </div>

                        <div className="col-xl-4 col-md-6">
                          <div className="form-floating form-floating-outline">
                            <select
                              className="form-select"
                              value={selectedAccount}
                              onChange={(e) => {
                                setSelectedAccount(e.target.value);
                                setPage(1);
                              }}
                            >
                              <option value="">All Accounts</option>

                              {banks.map((item) => (
                                <option key={item?.id} value={item?.id}>
                                  {getAccountName(item)}
                                  {item?.owner_name
                                    ? ` — ${item.owner_name}`
                                    : ""}
                                </option>
                              ))}
                            </select>

                            <label>
                              <i className="ri-bank-line me-1"></i>
                              Account
                            </label>
                          </div>
                        </div>

                        <div className="col-xl-2 col-md-6">
                          <div className="filter-result-box">
                            <i className="ri-list-check-2"></i>

                            <div>
                              <small>Showing</small>

                              <strong>{summary.currentPage}</strong>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* =================================================
                      TRANSFER TABLE
                  ================================================= */}

                  <div className="card border-0 shadow-sm rounded-4 overflow-hidden">
                    <div className="card-header bg-transparent border-0 px-4 pt-4 pb-3">
                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div>
                          <h5 className="fw-bold mb-1">
                            <i className="ri-list-check-2 text-primary me-2"></i>
                            Transfer History
                          </h5>

                          <small className="text-muted">
                            Complete record of account-to-account fund
                            movements.
                          </small>
                        </div>

                        <span className="record-badge">
                          {transfers?.total || 0} Records
                        </span>
                      </div>
                    </div>

                    <div className="card-body p-0">
                      {transfers?.data?.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table align-middle mb-0 transfer-table">
                            <thead>
                              <tr>
                                <th className="ps-4">#</th>

                                <th>Transfer Route</th>

                                <th>Amount</th>

                                <th>Date</th>

                                <th>Proof</th>

                                <th>Description</th>
                              </tr>
                            </thead>

                            <tbody>
                              {transfers.data.map((item, i) => (
                                <tr key={item?.id ?? i}>
                                  <td className="ps-4">
                                    <span className="row-number">
                                      {String(i + 1).padStart(2, "0")}
                                    </span>
                                  </td>

                                  {/* ROUTE */}

                                  <td>
                                    <div className="transfer-route">
                                      <div className="transfer-account from-account">
                                        <div className="route-icon">
                                          <i className="ri-bank-line"></i>
                                        </div>

                                        <div>
                                          <strong>
                                            {item?.from_bank?.account_no ||
                                              "--"}
                                          </strong>

                                          <small>
                                            {item?.from_bank?.owner_name ||
                                              "Unknown"}
                                          </small>
                                        </div>
                                      </div>

                                      <div className="route-arrow">
                                        <i className="ri-arrow-right-line"></i>
                                      </div>

                                      <div className="transfer-account to-account">
                                        <div className="route-icon">
                                          <i className="ri-bank-line"></i>
                                        </div>

                                        <div>
                                          <strong>
                                            {item?.to_bank?.account_no || "--"}
                                          </strong>

                                          <small>
                                            {item?.to_bank?.owner_name ||
                                              "Unknown"}
                                          </small>
                                        </div>
                                      </div>
                                    </div>
                                  </td>

                                  {/* AMOUNT */}

                                  <td>
                                    <div className="transfer-amount">
                                      <small>Transferred</small>

                                      <strong>
                                        ₹{" "}
                                        {Number(
                                          item?.amount || 0,
                                        ).toLocaleString("en-IN", {
                                          maximumFractionDigits: 2,
                                        })}
                                      </strong>
                                    </div>
                                  </td>

                                  {/* DATE */}

                                  <td>
                                    <div className="date-cell">
                                      <i className="ri-calendar-line"></i>

                                      <div>
                                        <strong>
                                          {formatDate(item?.transfer_date)}
                                        </strong>

                                        <small>Transfer Date</small>
                                      </div>
                                    </div>
                                  </td>

                                  {/* PROOF */}

                                  <td>
                                    {item?.img ? (
                                      <button
                                        type="button"
                                        className="proof-button"
                                        onClick={() =>
                                          setImgModal({
                                            show: true,
                                            src: `${imgBaseUrl}${item.img}`,
                                          })
                                        }
                                      >
                                        <span className="proof-icon">
                                          <i className="ri-image-line"></i>
                                        </span>

                                        <span>View Proof</span>
                                      </button>
                                    ) : (
                                      <span className="no-proof">
                                        <i className="ri-image-off-line me-1"></i>
                                        No Proof
                                      </span>
                                    )}
                                  </td>

                                  {/* DESCRIPTION */}

                                  <td>
                                    {item?.description ? (
                                      <div
                                        className="description-cell"
                                        title={item.description}
                                      >
                                        <i className="ri-file-text-line"></i>

                                        <span>{item.description}</span>
                                      </div>
                                    ) : (
                                      <span className="text-muted">--</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="empty-transfer-state">
                          <div className="empty-transfer-icon">
                            <i className="ri-exchange-funds-line"></i>
                          </div>

                          <h5 className="fw-bold mb-2">No Transfers Found</h5>

                          <p className="text-muted mb-0">
                            No fund transfers match the selected filters.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* =================================================
                        PAGINATION
                    ================================================= */}

                    {transfers?.data?.length > 0 && (
                      <div className="card-footer bg-transparent border-0 px-4 py-3">
                        <div className="pagination-wrapper">
                          <div className="pagination-info">
                            Showing <strong>{transfers?.from || 0}</strong> to{" "}
                            <strong>{transfers?.to || 0}</strong> of{" "}
                            <strong>{transfers?.total || 0}</strong> transfers
                          </div>

                          <nav>
                            <ul className="pagination pagination-sm mb-0">
                              {transfers?.links?.map((btn, i) => {
                                const isPrevious = i === 0;

                                const isNext = i === transfers.links.length - 1;

                                const isDisabled = !btn?.url;

                                return (
                                  <li
                                    key={i}
                                    className={`page-item ${btn?.active ? "active" : ""
                                      } ${isDisabled ? "disabled" : ""}`}
                                  >
                                    <button
                                      type="button"
                                      className="page-link"
                                      disabled={isDisabled}
                                      onClick={() => {
                                        if (isPrevious) {
                                          setPage(
                                            Math.max(
                                              1,
                                              Number(transfers.current_page) -
                                              1,
                                            ),
                                          );
                                        } else if (isNext) {
                                          setPage(
                                            Number(transfers.current_page) + 1,
                                          );
                                        } else {
                                          setPage(Number(btn.label));
                                        }
                                      }}
                                    >
                                      {isPrevious ? (
                                        <i className="ri-arrow-left-line"></i>
                                      ) : isNext ? (
                                        <i className="ri-arrow-right-line"></i>
                                      ) : (
                                        btn.label
                                      )}
                                    </button>
                                  </li>
                                );
                              })}
                            </ul>
                          </nav>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================
          NEW TRANSFER MODAL
      ========================================================= */}

      <div
        className="modal fade"
        id="FundTransferModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0 rounded-4 overflow-hidden">
            {/* HEADER */}

            <div className="modal-header transfer-modal-header">
              <div className="d-flex align-items-center gap-3">
                <div className="modal-transfer-icon">
                  <i className="ri-exchange-funds-line"></i>
                </div>

                <div>
                  <h5 className="modal-title fw-bold mb-0">
                    New Fund Transfer
                  </h5>

                  <small className="text-muted">
                    Move funds between business accounts
                  </small>
                </div>
              </div>

              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
              ></button>
            </div>

            <form onSubmit={handleTransferSubmit}>
              <div className="modal-body p-4">
                {/* ROUTE */}

                <div className="transfer-modal-route">
                  <div className="modal-route-side">
                    <div className="modal-route-icon from">
                      <i className="ri-bank-line"></i>
                    </div>

                    <span>SOURCE ACCOUNT</span>
                  </div>

                  <div className="modal-route-arrow">
                    <i className="ri-arrow-right-line"></i>
                  </div>

                  <div className="modal-route-side">
                    <div className="modal-route-icon to">
                      <i className="ri-bank-line"></i>
                    </div>

                    <span>DESTINATION</span>
                  </div>
                </div>

                <div className="row g-3 mt-2">
                  {/* FROM */}

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <select
                        className="form-select"
                        name="from_bank"
                        value={fromBank}
                        onChange={(e) => setFromBank(e.target.value)}
                        required
                      >
                        <option value="">Select Source Account</option>

                        {banks.map((b) => (
                          <option
                            key={b?.id}
                            value={b?.id}
                            disabled={String(b?.id) === String(toBank)}
                          >
                            {getAccountName(b)}
                            {b?.owner_name ? ` — ${b.owner_name}` : ""}
                          </option>
                        ))}
                      </select>

                      <label>From Account</label>
                    </div>
                  </div>

                  {/* TO */}

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <select
                        className="form-select"
                        name="to_bank"
                        value={toBank}
                        onChange={(e) => setToBank(e.target.value)}
                        required
                      >
                        <option value="">Select Destination</option>

                        {banks.map((b) => (
                          <option
                            key={b?.id}
                            value={b?.id}
                            disabled={String(b?.id) === String(fromBank)}
                          >
                            {getAccountName(b)}
                            {b?.owner_name ? ` — ${b.owner_name}` : ""}
                          </option>
                        ))}
                      </select>

                      <label>To Account</label>
                    </div>
                  </div>

                  {/* AMOUNT */}

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="number"
                        name="amount"
                        className="form-control transfer-amount-input"
                        placeholder="Amount"
                        min="0.01"
                        step="any"
                        required
                      />

                      <label>
                        <i className="ri-money-rupee-circle-line me-1"></i>
                        Transfer Amount
                      </label>
                    </div>
                  </div>

                  {/* DATE */}

                  <div className="col-md-6">
                    <div className="form-floating form-floating-outline">
                      <input
                        type="date"
                        name="transfer_date"
                        className="form-control"
                        required
                        defaultValue={new Date().toISOString().split("T")[0]}
                      />

                      <label>
                        <i className="ri-calendar-line me-1"></i>
                        Transfer Date
                      </label>
                    </div>
                  </div>

                  {/* PROOF */}

                  <div className="col-12">
                    <div className="proof-upload-box">
                      <div className="d-flex align-items-center gap-3">
                        <div className="upload-icon">
                          <i className="ri-image-add-line"></i>
                        </div>

                        <div>
                          <h6 className="fw-bold mb-1">Transfer Proof</h6>

                          <small className="text-muted">
                            Upload receipt, screenshot or transaction proof.
                          </small>
                        </div>
                      </div>

                      <input
                        type="file"
                        name="image"
                        className="form-control mt-3"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={(e) =>
                          setProofImage(e.target.files?.[0] || null)
                        }
                      />

                      {proofPreview && (
                        <div className="proof-preview mt-3">
                          <img src={proofPreview} alt="Proof Preview" />

                          <div>
                            <strong>Proof selected</strong>

                            <small>{proofImage?.name}</small>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* DESCRIPTION */}

                  <div className="col-12">
                    <div className="form-floating form-floating-outline">
                      <textarea
                        name="description"
                        className="form-control"
                        placeholder="Description"
                        style={{
                          minHeight: "90px",
                        }}
                      ></textarea>

                      <label>
                        <i className="ri-file-text-line me-1"></i>
                        Description / Remarks
                      </label>
                    </div>
                  </div>
                </div>

                {/* INFO */}

                <div className="transfer-info-box mt-4">
                  <i className="ri-information-line"></i>

                  <span>
                    Make sure the source and destination accounts are correct
                    before submitting the transfer.
                  </span>
                </div>
              </div>

              {/* FOOTER */}

              <div className="modal-footer px-4 py-3">
                <button
                  type="button"
                  className="btn btn-label-secondary"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="btn btn-primary px-4"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Processing...
                    </>
                  ) : (
                    <>
                      <i className="ri-exchange-funds-line me-1"></i>
                      Transfer Funds
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* =========================================================
          IMAGE PREVIEW MODAL
      ========================================================= */}

      <div
        className={`modal fade ${imgModal.show ? "show d-block" : ""}`}
        tabIndex="-1"
        style={{
          background: imgModal.show ? "rgba(0,0,0,.7)" : undefined,
        }}
      >
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content border-0 rounded-4 overflow-hidden">
            <div className="modal-header">
              <div>
                <h5 className="fw-bold mb-0">Transfer Proof</h5>

                <small className="text-muted">
                  Transaction supporting document
                </small>
              </div>

              <button
                type="button"
                className="btn-close"
                onClick={() =>
                  setImgModal({
                    show: false,
                    src: "",
                  })
                }
              ></button>
            </div>

            <div className="modal-body text-center p-3">
              {imgModal.src && (
                <img
                  src={imgModal.src}
                  alt="Transfer Proof"
                  className="transfer-proof-large"
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* =========================================================
          CSS
      ========================================================= */}

      <style>{`

        /* =====================================================
           PAGE
        ===================================================== */

        .fund-transfer-page {
          padding-top: 18px;
          padding-bottom: 30px;
        }

        .transfer-page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 20px;
          margin-bottom: 22px;
        }

        .transfer-title-wrapper {
          display: flex;
          align-items: center;
          gap: 15px;
        }

        .transfer-page-icon {
          width: 52px;
          height: 52px;
          min-width: 52px;
          border-radius: 15px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(var(--bs-primary-rgb), .11);
          color: var(--bs-primary);
          font-size: 25px;
        }

        .transfer-header-actions {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        /* =====================================================
           SUMMARY
        ===================================================== */

        .transfer-summary-card {
          height: 100%;
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 18px;
          border-radius: 15px;
          border: 1px solid var(--bs-border-color);
          background: var(
            --bs-card-bg,
            var(--bs-body-bg)
          );
          transition: .2s ease;
        }

        .transfer-summary-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 7px 20px rgba(0,0,0,.05);
        }

        .summary-icon {
          width: 46px;
          height: 46px;
          min-width: 46px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          font-size: 21px;
        }

        .summary-label {
          display: block;
          color: var(--bs-secondary-color);
          font-size: 11px;
          margin-bottom: 2px;
        }

        /* =====================================================
           FILTER
        ===================================================== */

        .filter-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 15px;
          margin-bottom: 8px;
        }

        .filter-result-box {
          height: 58px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          border-radius: 10px;
          background: rgba(
            var(--bs-primary-rgb),
            .055
          );
          color: var(--bs-primary);
        }

        .filter-result-box i {
          font-size: 20px;
        }

        .filter-result-box small {
          display: block;
          color: var(--bs-secondary-color);
          font-size: 9px;
        }

        .filter-result-box strong {
          font-size: 17px;
        }

        /* =====================================================
           TABLE
        ===================================================== */

        .transfer-table {
          min-width: 1150px;
        }

        .transfer-table thead th {
          padding: 13px 12px;
          font-size: 10px;
          text-transform: uppercase;
          letter-spacing: .5px;
          color: var(--bs-secondary-color);
          background: rgba(
            var(--bs-secondary-rgb),
            .035
          );
          border-bottom: 1px solid var(
            --bs-border-color
          );
          white-space: nowrap;
        }

        .transfer-table tbody td {
          padding: 16px 12px;
          border-bottom: 1px solid
            var(--bs-border-color-translucent);
        }

        .transfer-table tbody tr:last-child td {
          border-bottom: 0;
        }

        .transfer-table tbody tr {
          transition: .15s ease;
        }

        .transfer-table tbody tr:hover {
          background: rgba(
            var(--bs-primary-rgb),
            .025
          );
        }

        .row-number {
          width: 30px;
          height: 30px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 8px;
          background: rgba(
            var(--bs-secondary-rgb),
            .08
          );
          font-size: 10px;
          font-weight: 700;
          color: var(--bs-secondary-color);
        }

        /* =====================================================
           ROUTE
        ===================================================== */

        .transfer-route {
          display: flex;
          align-items: center;
          gap: 10px;
          min-width: 440px;
        }

        .transfer-account {
          display: flex;
          align-items: center;
          gap: 8px;
          min-width: 175px;
        }

        .route-icon {
          width: 35px;
          height: 35px;
          min-width: 35px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 9px;
          background: rgba(
            var(--bs-primary-rgb),
            .08
          );
          color: var(--bs-primary);
        }

        .transfer-account strong {
          display: block;
          max-width: 155px;
          overflow: hidden;
          text-overflow: ellipsis;
          font-size: 12px;
          font-weight: 700;
        }

        .transfer-account small {
          display: block;
          max-width: 155px;
          overflow: hidden;
          text-overflow: ellipsis;
          color: var(--bs-secondary-color);
          font-size: 9px;
          margin-top: 2px;
        }

        .route-arrow {
          width: 28px;
          height: 28px;
          min-width: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: rgba(
            var(--bs-success-rgb),
            .10
          );
          color: var(--bs-success);
        }

        /* =====================================================
           AMOUNT
        ===================================================== */

        .transfer-amount {
          min-width: 115px;
        }

        .transfer-amount small {
          display: block;
          color: var(--bs-secondary-color);
          font-size: 9px;
          margin-bottom: 2px;
        }

        .transfer-amount strong {
          font-size: 14px;
          color: var(--bs-success);
        }

        /* =====================================================
           DATE
        ===================================================== */

        .date-cell {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .date-cell > i {
          color: var(--bs-primary);
          font-size: 18px;
        }

        .date-cell strong {
          display: block;
          font-size: 11px;
        }

        .date-cell small {
          display: block;
          color: var(--bs-secondary-color);
          font-size: 9px;
        }

        /* =====================================================
           PROOF
        ===================================================== */

        .proof-button {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          border: 0;
          background: rgba(
            var(--bs-info-rgb),
            .08
          );
          color: var(--bs-info);
          border-radius: 8px;
          padding: 6px 9px;
          font-size: 10px;
          font-weight: 700;
          cursor: pointer;
        }

        .proof-button:hover {
          background: rgba(
            var(--bs-info-rgb),
            .16
          );
        }

        .proof-icon {
          font-size: 14px;
        }

        .no-proof {
          color: var(--bs-secondary-color);
          font-size: 10px;
        }

        /* =====================================================
           DESCRIPTION
        ===================================================== */

        .description-cell {
          display: flex;
          align-items: center;
          gap: 7px;
          max-width: 230px;
          color: var(--bs-secondary-color);
          font-size: 11px;
        }

        .description-cell i {
          color: var(--bs-primary);
          flex-shrink: 0;
        }

        .description-cell span {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        /* =====================================================
           PAGINATION
        ===================================================== */

        .pagination-wrapper {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 15px;
        }

        .pagination-info {
          color: var(--bs-secondary-color);
          font-size: 11px;
        }

        .pagination-info strong {
          color: var(--bs-body-color);
        }

        /* =====================================================
           EMPTY
        ===================================================== */

        .empty-transfer-state {
          min-height: 320px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          text-align: center;
          padding: 40px;
        }

        .empty-transfer-icon {
          width: 75px;
          height: 75px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 22px;
          margin-bottom: 18px;
          background: rgba(
            var(--bs-primary-rgb),
            .08
          );
          color: var(--bs-primary);
          font-size: 35px;
        }

        .record-badge {
          padding: 6px 10px;
          border-radius: 20px;
          background: rgba(
            var(--bs-secondary-rgb),
            .07
          );
          color: var(--bs-secondary-color);
          font-size: 10px;
          font-weight: 700;
        }

        /* =====================================================
           MODAL
        ===================================================== */

        .transfer-modal-header {
          padding: 18px 22px;
          border-bottom: 1px solid
            var(--bs-border-color);
          background: rgba(
            var(--bs-primary-rgb),
            .025
          );
        }

        .modal-transfer-icon {
          width: 43px;
          height: 43px;
          border-radius: 11px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(
            var(--bs-primary-rgb),
            .11
          );
          color: var(--bs-primary);
          font-size: 21px;
        }

        /* =====================================================
           MODAL ROUTE
        ===================================================== */

        .transfer-modal-route {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 25px;
          padding: 15px;
          border-radius: 13px;
          background: rgba(
            var(--bs-primary-rgb),
            .035
          );
        }

        .modal-route-side {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
        }

        .modal-route-side > span {
          font-size: 9px;
          font-weight: 700;
          color: var(--bs-secondary-color);
        }

        .modal-route-icon {
          width: 42px;
          height: 42px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 11px;
          font-size: 20px;
        }

        .modal-route-icon.from {
          background: rgba(
            var(--bs-danger-rgb),
            .10
          );
          color: var(--bs-danger);
        }

        .modal-route-icon.to {
          background: rgba(
            var(--bs-success-rgb),
            .10
          );
          color: var(--bs-success);
        }

        .modal-route-arrow {
          width: 35px;
          height: 35px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 50%;
          background: rgba(
            var(--bs-primary-rgb),
            .10
          );
          color: var(--bs-primary);
        }

        .transfer-amount-input {
          font-size: 17px;
          font-weight: 700;
        }

        /* =====================================================
           PROOF UPLOAD
        ===================================================== */

        .proof-upload-box {
          padding: 16px;
          border: 1px dashed
            var(--bs-border-color);
          border-radius: 13px;
          background: rgba(
            var(--bs-secondary-rgb),
            .025
          );
        }

        .upload-icon {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10px;
          background: rgba(
            var(--bs-info-rgb),
            .10
          );
          color: var(--bs-info);
          font-size: 20px;
        }

        .proof-preview {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 9px;
          border-radius: 9px;
          background: rgba(
            var(--bs-success-rgb),
            .07
          );
        }

        .proof-preview img {
          width: 60px;
          height: 60px;
          object-fit: cover;
          border-radius: 7px;
          background: #fff;
        }

        .proof-preview strong {
          display: block;
          font-size: 11px;
        }

        .proof-preview small {
          display: block;
          color: var(--bs-secondary-color);
          font-size: 9px;
          max-width: 280px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .transfer-info-box {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          padding: 10px 12px;
          border-radius: 9px;
          background: rgba(
            var(--bs-info-rgb),
            .07
          );
          color: var(--bs-info);
          font-size: 10px;
        }

        /* =====================================================
           LARGE IMAGE
        ===================================================== */

        .transfer-proof-large {
          max-width: 100%;
          max-height: 70vh;
          object-fit: contain;
          border-radius: 8px;
        }

        /* =====================================================
           MOBILE
        ===================================================== */

        @media (max-width: 767.98px) {

          .transfer-page-header {
            flex-direction: column;
            align-items: flex-start;
          }

          .transfer-header-actions {
            width: 100%;
            flex-wrap: wrap;
          }

          .transfer-header-actions .btn-primary {
            flex: 1;
          }

          .transfer-title-wrapper {
            align-items: flex-start;
          }

          .transfer-page-icon {
            width: 45px;
            height: 45px;
            min-width: 45px;
          }

          .transfer-title-wrapper h3 {
            font-size: 20px;
          }

          .filter-header {
            align-items: flex-start;
            flex-direction: column;
          }

          .pagination-wrapper {
            flex-direction: column;
            align-items: flex-start;
          }

          .transfer-modal-route {
            gap: 15px;
          }

        }

      `}</style>
    </>
  );
}
